/**
 * Componentes del module kind `forum` (F3-C).
 *
 * - `ForumModuleEditor` (autoría): título + cerrar/abrir el foro. Auto-crea la
 *   fila `forum` (id determinista por módulo) si aún no existe.
 * - `ForumModuleViewer` (player): lista de hilos del CONTEXTO de cohorte del
 *   visor (+ course-wide) y vista de hilo con respuestas anidadas 1 nivel +
 *   composer. Moderación (profesor/autor): fijar/cerrar hilos y ocultar posts.
 *
 * Local-first: lee/escribe las filas sincronizadas; el write-path aplica la
 * authz real (participante/moderador; el alumno no fija pinned/locked/hidden_*).
 */
import { type Component, createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { EyeOff, Lock, LockOpen, MessageSquare, Pin, Reply, Send } from "lucide-solid";
import {
  createPostMutation,
  createThreadMutation,
  ensureForumMutation,
  forumIdForModule,
  groupThreadPosts,
  setPostHiddenMutation,
  updateForumMutation,
  updateThreadMutation,
} from "@space/shared";
import type { ForumPost } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import {
  useAmICourseAuthor,
  useForumByModule,
  useForumThreads,
  useMyStudentCohortForCourse,
  useMyTeachingCohortForCourse,
  useThreadPosts,
} from "../hooks/use-community-data";
import type { ModuleComponentProps } from "./registry";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

// ─── Autoría ──────────────────────────────────────────────────────────────────

export const ForumModuleEditor: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const forum = useForumByModule(() => props.moduleId);

  // Garantiza la fila `forum` del módulo si aún no existe (id determinista;
  // NO crea otro módulo). Guard `ensured` para no reinsertar en re-runs.
  let ensured = false;
  createEffect(() => {
    const db = drizzle();
    if (db && !ensured && forum.data === null && !forum.isLoading) {
      ensured = true;
      void ensureForumMutation(db, {
        moduleId: props.moduleId,
        courseId: props.courseId,
      });
    }
  });

  const save = (patch: { title?: string; locked?: boolean }) => {
    const db = drizzle();
    const id = forum.data?.id ?? forumIdForModule(props.moduleId);
    if (db) {
      void updateForumMutation(db, id, patch);
    }
  };

  return (
    <div class="space-y-2 rounded-lg bg-muted/30 p-3" data-testid="forum-editor">
      <input
        class={field}
        data-testid="edit-forum-title"
        value={forum.data?.title ?? ""}
        onChange={(e) => save({ title: e.currentTarget.value })}
        placeholder={t(msg`Forum title`)}
      />
      <label class="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          data-testid="edit-forum-locked"
          checked={Boolean(forum.data?.locked)}
          onChange={(e) => save({ locked: e.currentTarget.checked })}
        />
        {t(msg`Lock the forum (no new threads)`)}
      </label>
    </div>
  );
};

// ─── Player ───────────────────────────────────────────────────────────────────

export const ForumModuleViewer: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const forum = useForumByModule(() => props.moduleId);
  const studentCohort = useMyStudentCohortForCourse(() => props.courseId);
  const teachingCohort = useMyTeachingCohortForCourse(() => props.courseId);
  const author = useAmICourseAuthor(() => props.courseId);

  const cohortId = (): string | null =>
    teachingCohort.data?.cohort_id ?? studentCohort.data?.cohort_id ?? null;
  const orgId = (): string | null =>
    teachingCohort.data?.organization_id ??
    studentCohort.data?.organization_id ??
    forum.data?.organization_id ??
    null;
  const canModerate = () => Boolean(teachingCohort.data) || Boolean(author.data);
  const isParticipant = () => Boolean(studentCohort.data) || canModerate();

  const forumId = () => forum.data?.id;
  const threads = useForumThreads(forumId, cohortId);

  const [selectedThread, setSelectedThread] = createSignal<string | null>(null);
  const [newThreadTitle, setNewThreadTitle] = createSignal("");

  const createThread = () => {
    const db = drizzle();
    const fid = forumId();
    const uid = userId();
    const title = newThreadTitle().trim();
    if (!db || !fid || !uid || !title) {
      return;
    }
    void (async () => {
      const id = await createThreadMutation(db, {
        forumId: fid,
        cohortId: cohortId(),
        organizationId: orgId(),
        authorId: uid,
        title,
      });
      setNewThreadTitle("");
      setSelectedThread(id);
    })();
  };

  return (
    <div
      class="space-y-3 rounded-2xl border border-border bg-surface p-4"
      data-testid="forum-player"
    >
      <Show
        when={selectedThread()}
        fallback={
          <>
            <div class="flex items-center gap-2 font-semibold">
              <MessageSquare class="h-4 w-4 text-accent" />
              <span>{forum.data?.title ?? t(msg`Forum`)}</span>
            </div>

            <Show
              when={isParticipant()}
              fallback={
                <p class="text-sm text-muted-foreground" data-testid="forum-not-enrolled">
                  {t(msg`Enroll in this cohort to join the discussion.`)}
                </p>
              }
            >
              <Show when={!forum.data?.locked}>
                <div class="flex gap-2">
                  <input
                    class={field}
                    data-testid="forum-new-thread-title"
                    value={newThreadTitle()}
                    onInput={(e) => setNewThreadTitle(e.currentTarget.value)}
                    placeholder={t(msg`Start a new thread…`)}
                  />
                  <button
                    type="button"
                    data-testid="forum-create-thread"
                    onClick={createThread}
                    disabled={!newThreadTitle().trim()}
                    class="shrink-0 rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground disabled:opacity-50"
                  >
                    {t(msg`Post`)}
                  </button>
                </div>
              </Show>
            </Show>

            <ul class="space-y-1.5" data-testid="forum-thread-list">
              <For
                each={threads.data ?? []}
                fallback={<li class="text-sm text-muted-foreground">{t(msg`No threads yet.`)}</li>}
              >
                {(thread) => (
                  <li>
                    <button
                      type="button"
                      data-testid="forum-thread-item"
                      onClick={() => setSelectedThread(thread.id)}
                      class="flex w-full items-center gap-2 rounded-xl border border-border px-3 py-2 text-left text-sm hover:bg-accent/10"
                    >
                      <Show when={thread.pinned}>
                        <Pin class="h-3.5 w-3.5 text-accent" />
                      </Show>
                      <Show when={thread.locked}>
                        <Lock class="h-3.5 w-3.5 text-muted-foreground" />
                      </Show>
                      <span class="flex-1 font-medium">{thread.title}</span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </>
        }
      >
        {(threadId) => (
          <ThreadView
            threadId={threadId()}
            forumId={forumId() ?? ""}
            cohortId={cohortId()}
            organizationId={orgId()}
            canModerate={canModerate()}
            isParticipant={isParticipant()}
            onBack={() => setSelectedThread(null)}
          />
        )}
      </Show>
    </div>
  );
};

// ─── Vista de hilo (posts anidados 1 nivel + composer + moderación) ───────────

interface ThreadViewProps {
  threadId: string;
  forumId: string;
  cohortId: string | null;
  organizationId: string | null;
  canModerate: boolean;
  isParticipant: boolean;
  onBack: () => void;
}

const ThreadView: Component<ThreadViewProps> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const posts = useThreadPosts(() => props.threadId);
  const threads = useForumThreads(
    () => props.forumId,
    () => props.cohortId,
  );
  const thread = createMemo(() => (threads.data ?? []).find((th) => th.id === props.threadId));

  const [body, setBody] = createSignal("");
  const [replyTo, setReplyTo] = createSignal<string | null>(null);

  // Anidamiento de 1 nivel (selector puro compartido y testeado).
  const grouped = createMemo(() => groupThreadPosts(posts.data ?? []));
  const topLevel = createMemo(() => grouped().topLevel);
  const repliesByParent = createMemo(() => grouped().repliesByParent);

  const submit = () => {
    const db = drizzle();
    const uid = userId();
    const text = body().trim();
    if (!db || !uid || !text) {
      return;
    }
    void (async () => {
      await createPostMutation(db, {
        threadId: props.threadId,
        forumId: props.forumId,
        cohortId: props.cohortId,
        organizationId: props.organizationId,
        authorId: uid,
        body: text,
        replyToId: replyTo(),
      });
      setBody("");
      setReplyTo(null);
    })();
  };

  const toggleHidden = (post: ForumPost) => {
    const db = drizzle();
    if (db) {
      void setPostHiddenMutation(db, post.id, !post.hidden_at);
    }
  };

  const togglePinned = () => {
    const db = drizzle();
    const th = thread();
    if (db && th) {
      void updateThreadMutation(db, th.id, { pinned: !th.pinned });
    }
  };
  const toggleLocked = () => {
    const db = drizzle();
    const th = thread();
    if (db && th) {
      void updateThreadMutation(db, th.id, { locked: !th.locked });
    }
  };

  const PostRow: Component<{ post: ForumPost; nested?: boolean }> = (p) => (
    <li
      data-testid="forum-post"
      data-hidden={p.post.hidden_at ? "true" : "false"}
      classList={{ "ml-6 border-l-2 border-border pl-3": p.nested }}
      class="rounded-lg bg-muted/20 p-2.5 text-sm"
    >
      <Show
        when={!p.post.hidden_at}
        fallback={
          <p class="italic text-muted-foreground" data-testid="forum-post-hidden">
            {t(msg`This message was hidden by a moderator.`)}
            <Show when={props.canModerate}>
              {" "}
              <button
                type="button"
                data-testid="forum-unhide-post"
                onClick={() => toggleHidden(p.post)}
                class="underline"
              >
                {t(msg`Unhide`)}
              </button>
            </Show>
          </p>
        }
      >
        <p class="whitespace-pre-wrap" data-testid="forum-post-body">
          {p.post.body}
        </p>
        <div class="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          <Show when={props.isParticipant && !p.nested && !thread()?.locked}>
            <button
              type="button"
              data-testid="forum-reply-post"
              onClick={() => setReplyTo(p.post.id)}
              class="inline-flex items-center gap-1 hover:text-foreground"
            >
              <Reply class="h-3 w-3" /> {t(msg`Reply`)}
            </button>
          </Show>
          <Show when={props.canModerate}>
            <button
              type="button"
              data-testid="forum-hide-post"
              onClick={() => toggleHidden(p.post)}
              class="inline-flex items-center gap-1 hover:text-danger"
            >
              <EyeOff class="h-3 w-3" /> {t(msg`Hide`)}
            </button>
          </Show>
        </div>
      </Show>
    </li>
  );

  return (
    <div class="space-y-3">
      <div class="flex items-center gap-2">
        <button
          type="button"
          data-testid="forum-thread-back"
          onClick={() => {
            props.onBack();
          }}
          class="text-sm text-muted-foreground hover:text-foreground"
        >
          ← {t(msg`Threads`)}
        </button>
        <span class="flex-1 font-semibold">{thread()?.title}</span>
        <Show when={props.canModerate && thread()}>
          <button
            type="button"
            data-testid="forum-toggle-pin"
            onClick={togglePinned}
            class="text-muted-foreground hover:text-accent"
            title={t(msg`Pin thread`)}
          >
            <Pin class="h-4 w-4" classList={{ "text-accent": Boolean(thread()?.pinned) }} />
          </button>
          <button
            type="button"
            data-testid="forum-toggle-lock"
            onClick={toggleLocked}
            class="text-muted-foreground hover:text-accent"
            title={t(msg`Lock thread`)}
          >
            <Show when={thread()?.locked} fallback={<LockOpen class="h-4 w-4" />}>
              <Lock class="h-4 w-4 text-accent" />
            </Show>
          </button>
        </Show>
      </div>

      <ul class="space-y-2" data-testid="forum-post-list">
        <For
          each={topLevel()}
          fallback={<li class="text-sm text-muted-foreground">{t(msg`No replies yet.`)}</li>}
        >
          {(post) => (
            <>
              <PostRow post={post} />
              <For each={repliesByParent().get(post.id) ?? []}>
                {(reply) => <PostRow post={reply} nested />}
              </For>
            </>
          )}
        </For>
      </ul>

      <Show
        when={props.isParticipant && !thread()?.locked}
        fallback={
          <Show when={thread()?.locked}>
            <p class="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock class="h-3 w-3" /> {t(msg`This thread is locked.`)}
            </p>
          </Show>
        }
      >
        <div class="flex gap-2">
          <textarea
            class={field}
            rows={2}
            data-testid="forum-post-body-input"
            value={body()}
            onInput={(e) => setBody(e.currentTarget.value)}
            placeholder={replyTo() ? t(msg`Write a reply…`) : t(msg`Write a message…`)}
          />
          <button
            type="button"
            data-testid="forum-submit-post"
            onClick={submit}
            disabled={!body().trim()}
            class="shrink-0 self-start rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground disabled:opacity-50"
          >
            <Send class="h-4 w-4" />
          </button>
        </div>
        <Show when={replyTo()}>
          <button
            type="button"
            onClick={() => setReplyTo(null)}
            class="text-xs text-muted-foreground underline"
          >
            {t(msg`Cancel reply`)}
          </button>
        </Show>
      </Show>
    </div>
  );
};
